import { CSSProperties } from 'react';
import { Folder } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useAgentWorkspaceFiles, type AgentWorkspaceFileItem } from '../../../lib/agent-workspace-files';
import { displayName, extensionOf } from '../../../lib/artifact-file';
import { fileTypeKind } from '../../../lib/artifact-image';
import { createdAtMs, fileStats } from '../../../lib/summary-freshness';
import { useNewSince } from '../../../lib/use-last-visited';
import { timeAgo } from '../../../lib/notification-time';
import { openFileForRow } from '../../../lib/doc-viewer-presenter';
import { Badge, Button, Card } from '../ui';
import styles from './Home2FilesCard.module.css';

// Home 2 (symphonyHome2Web) — the Files bento card (Figma 1859:2833), the right slot of Row 2. A preview
// of the most recent files, each with its type icon, name, and a "New" badge or relative time, over a
// "{total} files · {new} new" summary and an "Open Files" action. Clicking a row opens the file in the
// doc viewer. Real data via useAgentWorkspaceFiles; mirrors the data plumbing of FilesSummaryCard.

const CARD: CSSProperties = {
  borderColor: 'var(--ds-separator-default)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};
const HEADER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' };
const HEADER_LEFT: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const TITLE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 16,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '20px',
  letterSpacing: '-0.32px',
  color: 'rgb(var(--ink-rgb))',
};
const LIST: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, width: '100%' };
const ROW: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' };
const ROW_LEFT: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 };
// The file-type glyph tile — the design's rounded gray square (Figma 1861:8729). Rendered for every row
// (the mock's bare colored squares on rows 2–3 are placeholder inconsistency).
const TILE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 4,
  borderRadius: 8,
  background: 'var(--ds-surface-standard-bg)',
  flexShrink: 0,
};
const NAME: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  color: 'rgb(var(--ink-rgb))',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};
const TIME: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 11,
  color: 'var(--ds-text-secondary)',
  flexShrink: 0,
  whiteSpace: 'nowrap',
};
// `marginTop: auto` sinks the divider + footer to the card's bottom, so the footer stays aligned when
// the card is stretched to match a taller sibling in the row.
const DIVIDER: CSSProperties = { height: 1, width: '100%', background: 'var(--ds-separator-light)', flexShrink: 0, marginTop: 'auto' };
const FOOTER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' };
const SUMMARY: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgb(var(--ink-rgb))',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const PREVIEW_COUNT = 3;

export function Home2FilesCard() {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const state = useAgentWorkspaceFiles();
  const since = useNewSince('files');

  if (state.status !== 'ready' || state.items.length === 0) return null;

  const dateMs = (f: AgentWorkspaceFileItem) => Date.parse(f.updatedDate ?? f.createdDate ?? '') || 0;
  const rows = [...state.items].sort((a, b) => dateMs(b) - dateMs(a)).slice(0, PREVIEW_COUNT);
  // Before the first-visit effect resolves, `since` is null → nothing counts as new (no badge flash).
  const sinceMs = since ?? Number.POSITIVE_INFINITY;
  const stats = fileStats(state.items, sinceMs);
  const isNew = (f: AgentWorkspaceFileItem) => {
    const ms = createdAtMs(f);
    return ms !== null && ms > sinceMs;
  };

  const open = (f: AgentWorkspaceFileItem) => {
    logPageClickBi({ page_name: 'home', button_name: 'open_file' });
    openFileForRow(f, 'home');
  };

  return (
    <Card style={CARD}>
      <div style={HEADER}>
        <span style={HEADER_LEFT}>
          <Folder size={16} aria-hidden style={{ color: 'rgb(var(--ink-rgb))', flexShrink: 0 }} />
          <span style={TITLE}>{t('files.title')}</span>
        </span>
      </div>

      <div style={LIST}>
        {rows.map((file) => (
          <div
            key={file.fileId ?? file.name}
            style={ROW}
            className={styles.fileRow}
            role="button"
            tabIndex={0}
            onClick={() => open(file)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                open(file);
              }
            }}
          >
            <span style={ROW_LEFT}>
              <span style={TILE}>
                <img
                  src={`${basePath}/file-types/${fileTypeKind(extensionOf(file))}.png`}
                  alt=""
                  width={24}
                  height={24}
                  style={{ display: 'block' }}
                />
              </span>
              <span className={styles.fileName} style={NAME}>
                {displayName(file)}
              </span>
            </span>
            {isNew(file) ? (
              <Badge variant="connected" label={t('home2.files.new')} size="tiny" />
            ) : (
              <span style={TIME}>{timeAgo(file.updatedDate ?? file.createdDate ?? null)}</span>
            )}
          </div>
        ))}
      </div>

      <div style={DIVIDER} />

      <div style={FOOTER}>
        <span style={SUMMARY}>{t('home2.files.summary', { total: stats.files, new: stats.newCount })}</span>
        <Button
          size="medium"
          href={`${basePath}/files`}
          onClick={() => logPageClickBi({ page_name: 'home', button_name: 'open_files' })}
        >
          {t('home2.files.open')}
        </Button>
      </div>
    </Card>
  );
}
